import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, ArrowRight, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { Field, Textarea, Select } from "@/components/Field";
import { useAdminData, TableSkeletonRows } from "@/components/admin/useAdminData";

const STATUSES = ["new", "reviewing", "info_required", "offer_prepared", "offer_sent", "accepted", "rejected", "expired", "converted"];

export default function Quotes() {
  const { t, sl } = useI18n();
  const nav = useNavigate();
  const { items: quotes, loading, reload: load } = useAdminData("/quotes");
  const [active, setActive] = useState(null);

  const del = async (e, q) => {
    e.stopPropagation();
    if (!window.confirm(`Anfrage von „${q.name}" wirklich dauerhaft löschen? Diese Aktion kann nicht rückgängig gemacht werden.`)) return;
    try { await api.delete(`/quotes/${q.id}`); toast.success("Anfrage gelöscht"); load(); }
    catch (err) { toast.error(apiErr(err)); }
  };

  const update = async (patch) => {
    try { const { data } = await api.put(`/quotes/${active.id}`, patch); setActive(data); load(); toast.success("OK"); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const toOffer = () => {
    localStorage.setItem("jd_offer_prefill", JSON.stringify({
      customer_id: active.customer_id || "", customer_name: active.name || active.customer_name || "",
      title: active.project_type || "Angebot", notes: active.description || "",
    }));
    nav("/admin/offers?new=1");
  };

  return (
    <div className="space-y-5">
      <h1 className="font-display text-2xl font-black">{t("admin.quotes")}</h1>
      <div className="surface overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary text-left text-muted-foreground"><tr><th className="p-4">{t("common.name")}</th><th className="p-4">Typ</th><th className="p-4">Budget</th><th className="p-4">{t("common.status")}</th><th className="p-4">{t("common.date")}</th></tr></thead>
          <tbody>
            {loading ? <TableSkeletonRows cols={5} /> : quotes.map((q) => (
              <tr key={q.id} onClick={() => setActive(q)} className="border-t border-border cursor-pointer hover:bg-secondary" data-testid={`quote-${q.id}`}>
                <td className="p-4 font-medium">{q.name}</td><td className="p-4">{q.project_type}</td><td className="p-4">{q.budget}</td>
                <td className="p-4"><span className="rounded-full bg-primary/15 text-primary px-2.5 py-0.5 text-xs font-semibold capitalize">{sl(q.status || "new")}</span></td>
                <td className="p-4 text-muted-foreground"><div className="flex items-center justify-between gap-2">{(q.created_at || "").slice(0, 10)}<button onClick={(e) => del(e, q)} className="btn-ghost !py-1.5 !px-2.5 text-xs text-red-500 hover:text-red-600" data-testid={`quote-delete-${q.id}`}><Trash2 size={13} /></button></div></td>
              </tr>
            ))}
            {!loading && quotes.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-muted-foreground">{t("common.none")}</td></tr>}
          </tbody>
        </table>
      </div>
      {active && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="absolute inset-0 bg-black/50" onClick={() => setActive(null)} />
          <div className="relative w-full max-w-lg bg-background border-l border-border h-full overflow-y-auto p-6 space-y-4" data-testid="quote-drawer">
            <div className="flex items-center justify-between"><h2 className="font-display text-xl font-black">{active.name}</h2><button onClick={() => setActive(null)}><X size={20} /></button></div>
            <div className="text-sm space-y-1 text-muted-foreground">
              <p>{active.email} · {active.phone}</p>
              <p><b>{t("quote.projectType")}:</b> {active.project_type} · {active.platform}</p>
              <p><b>{t("quote.budget")}:</b> {active.budget}</p>
              {active.features && <p><b>{t("quote.features")}:</b> {active.features}</p>}
              <p className="pt-2 text-foreground">{active.description}</p>
            </div>
            <Field label={t("common.status")}><Select testid="quote-status" value={active.status || "new"} onChange={(e) => update({ status: e.target.value })}>{STATUSES.map((s) => <option key={s} value={s}>{sl(s)}</option>)}</Select></Field>
            <Field label="Interne Notizen"><Textarea testid="quote-notes" defaultValue={active.internal_notes || ""} onBlur={(e) => update({ internal_notes: e.target.value })} /></Field>
            <button onClick={toOffer} className="btn-primary w-full" data-testid="quote-to-offer">Angebot erstellen <ArrowRight size={16} /></button>
          </div>
        </div>
      )}
    </div>
  );
}
